import type { Locale } from '@infiltre/shared';
import { useDialog } from '@/shared/lib/dialog/use-dialog';
import { useT } from '@/shared/lib/i18n';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { HandKicker } from './HandKicker';
import { InkCircle } from './InkCircle';
import { LanguageCta } from './LanguageCta';
import { PhaseTag } from './PhaseTag';
import { Pin } from './Pin';

export type DossierBriefingPanelProps = {
  /** 1 / 2 / 3 — drives MODE rows 01-03. */
  infiltreCount: number;
  onInfiltreCountChange: (n: number) => void;
  includeClochard: boolean;
  onIncludeClochardChange: (v: boolean) => void;
  /** 30 / 60 / 90 / 120 — drives the timer pills. Other values still saved but no pill highlights. */
  discussionSeconds: number;
  onDiscussionSecondsChange: (s: number) => void;
  /** Room language — shown as a polaroid CTA that opens the picker. */
  locale: Locale;
  onLocaleChange: (l: Locale) => void;
  /** "prêt — N suspects" footer. */
  playerCount: number;
  /** Min players to enable the start CTA. */
  minPlayers?: number;
  onStart: () => void;
  /** Disables the entire fieldset (mid-game, non-host). */
  disabled?: boolean;
  /** Panel rotation in degrees. Default `2` (cork-stage tilt). Pass `0`
   *  when rendered inside a mobile sheet so it lays flat. */
  tilt?: number;
  /** Hide the bottom "ready / DISTRIBUER" footer. Used by the mobile
   *  sheet — `MobileLobbyFooter` already renders the start CTA outside
   *  the sheet, so a duplicate inside is noise. */
  hideFooter?: boolean;
};

const TIMER_OPTIONS: readonly number[] = [30, 60, 90, 120];

/**
 * Briefing settings paper — shared by desktop lobby aside and mobile
 * host-settings sheet. Paper-ruled with two pins, PHASE 00 / BRIEFING
 * header, then sections: language polaroid, numbered mode list
 * (01..03 = infiltreCount), timer pills, Clochard checkbox, and an
 * orange DISTRIBUER → CTA. Drop into the right column of the cork
 * stage at ~410-460px wide, rotate +1.4°/+2° (desktop only).
 */
export const DossierBriefingPanel = ({
  infiltreCount,
  onInfiltreCountChange,
  includeClochard,
  onIncludeClochardChange,
  discussionSeconds,
  onDiscussionSecondsChange,
  locale,
  onLocaleChange,
  playerCount,
  minPlayers = 3,
  onStart,
  disabled,
  tilt = 2,
  hideFooter = false,
}: DossierBriefingPanelProps) => {
  const t = useT();
  const dialog = useDialog();

  const openLanguagePicker = (): void => {
    void dialog.open('language-picker', { value: locale, onChange: onLocaleChange });
  };

  const modeRows: ReadonlyArray<{
    n: string;
    label: string;
    active: boolean;
    onClick: () => void;
  }> = [
    {
      n: '01',
      label: t('briefing.mode.classic'),
      active: infiltreCount === 1,
      onClick: () => onInfiltreCountChange(1),
    },
    {
      n: '02',
      label: t('briefing.mode.duo'),
      active: infiltreCount === 2,
      onClick: () => onInfiltreCountChange(2),
    },
    {
      n: '03',
      label: t('briefing.mode.trio'),
      active: infiltreCount === 3,
      onClick: () => onInfiltreCountChange(3),
    },
  ];

  const ready = playerCount >= minPlayers;

  return (
    <div
      className="paper-dossier paper-ruled relative flex flex-col gap-5 px-7 py-6 text-ink"
      style={{ transform: tilt ? `rotate(${tilt}deg)` : undefined }}
    >
      <Pin left={28} top={-6} />
      <Pin left={'calc(100% - 28px)'} top={-6} />

      {/* Language polaroid — absolute sticker pinned to top-right of the
          dossier, alongside the BRIEFING title rather than as a labeled row. */}
      <div className="absolute top-3 right-4 z-10">
        <LanguageCta value={locale} onClick={openLanguagePicker} size="sm" />
      </div>

      <header className="flex flex-col gap-1 pr-[110px]">
        <PhaseTag size={11} color="rgba(0,0,0,.5)">
          {t('briefing.phase_00')}
        </PhaseTag>
        <BrandMark size={'clamp(40px, 4vw, 50px)'} className="mt-1">
          {t('briefing.title')}
        </BrandMark>
        <HandKicker size={20} color="var(--color-pencil)" rot={0} className="mt-2">
          — {t('briefing.subtitle')}
        </HandKicker>
      </header>

      <fieldset disabled={disabled} className="flex contents min-h-0 flex-1 flex-col gap-5">
        {/* MODE list */}
        <section className="relative">
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('briefing.mode.label')}
          </PhaseTag>
          <ul className="mt-2 flex flex-col">
            {modeRows.map((row) => (
              <li key={row.n}>
                <button
                  type="button"
                  onClick={row.onClick}
                  disabled={disabled}
                  className="block w-full border-ink/85 border-b py-1.5 text-left font-type text-[15px] text-ink tracking-[0.04em] transition-opacity disabled:cursor-not-allowed"
                  style={{ opacity: row.active ? 1 : 0.55 }}
                >
                  <span className="relative inline-block">
                    {row.n}.
                    {row.active && (
                      <InkCircle
                        auto
                        rot={-6}
                        delay={120}
                        color="var(--color-magenta)"
                        strokeWidth={3}
                        padX={6}
                        padY={2}
                      />
                    )}
                  </span>
                  &nbsp;&nbsp;{row.label}
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Timer pills */}
        <section className="relative">
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('briefing.timer.label')}
          </PhaseTag>
          <div className="mt-2 flex">
            {TIMER_OPTIONS.map((seconds) => {
              const active = discussionSeconds === seconds;
              return (
                <div
                  key={seconds}
                  className="flex flex-1 items-center justify-center border-ink border-b"
                >
                  <button
                    type="button"
                    onClick={() => onDiscussionSecondsChange(seconds)}
                    disabled={disabled}
                    className="block w-full py-1.5 text-center font-type text-[18px] text-ink transition-opacity disabled:cursor-not-allowed"
                    style={{ opacity: active ? 1 : 0.45 }}
                  >
                    <span className="relative inline-block">
                      {seconds}s
                      {active && (
                        <InkCircle
                          auto
                          rot={3}
                          delay={150}
                          color="var(--color-magenta)"
                          strokeWidth={3}
                          padX={8}
                          padY={3}
                        />
                      )}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Mr. Blanc checkbox row */}
        <section>
          <button
            type="button"
            onClick={() => onIncludeClochardChange(!includeClochard)}
            disabled={disabled}
            className="block w-full border-ink/85 border-b py-2 text-left font-type text-[14px] text-ink"
          >
            <span className="inline-block w-6">{includeClochard ? '☑' : '☐'}</span>
            <span style={{ textDecoration: includeClochard ? 'none' : 'line-through' }}>
              {t('briefing.clochard.label')}
            </span>{' '}
            <span className="text-ink-soft">{t('briefing.clochard.hint')}</span>
          </button>
        </section>
      </fieldset>

      {/* Footer */}
      {!hideFooter && (
        <footer className="flex items-center justify-between gap-3 pt-1">
          <HandKicker
            size={18}
            color="var(--color-pencil)"
            rot={-1}
            className="max-w-[140px] leading-[1.05]"
          >
            {ready
              ? t('briefing.footer.ready', { count: String(playerCount) })
              : t('briefing.footer.need_more', { count: String(minPlayers - playerCount) })}
          </HandKicker>
          <Button
            variant="orange"
            fullWidth={false}
            onClick={onStart}
            disabled={disabled || !ready}
          >
            {t('briefing.footer.deal')} →
          </Button>
        </footer>
      )}
    </div>
  );
};
